// Copyright (c) Docugami, Inc. All rights reserved.

import { createFileInfoFromDocument, createFolderIdString, getDirectoryName, getDirectoryPath, getFileName, getParentDirectoryPath, isAncestor, isChild, isDgDirectory, isDgFile, isDirectory, UnassignedDocSetId } from "components/file_management/file_helpers";
import { DocumentStatus, FileChangedMessage, FileInfo, FileManagerDataApi, IngestionIssues, IngestionStatus, IngestionWarnings } from "networking/api";
import { Event, IEvent } from "utils/Event";
import { Logger, LoggerCategory } from "utils/Logger";
import { FileUploaderInstance, FileUploadEvent, FileUploadStatus } from "./FileUploader";

export interface DirectoryProperties {
    name: string,
    displayName?: string,
    path: string,
    parentPath?: string,
    isHidden: boolean
}

export enum DirectoryStatus {
    Uninitialized,
    Initializing,
    Initialized
}

export class DgDirectory {
    private _properties: DirectoryProperties;
    private _status: DirectoryStatus = DirectoryStatus.Uninitialized;
    private readonly _onStatusChangedEvent: Event<[DirectoryStatus, DirectoryStatus]> = new Event();

    constructor(path: string) {
        const parentPath = getParentDirectoryPath(path);
        const name = getDirectoryName(path);
        this._properties = {
            name: name,
            path: path,
            parentPath: !!parentPath ? parentPath : undefined,
            isHidden: isNameHidden(name)
        };
    }

    get properties(): DirectoryProperties { return this._properties; }
    get onStatusChanged(): IEvent<[DirectoryStatus, DirectoryStatus]> { return this._onStatusChangedEvent; }
    get status(): DirectoryStatus { return this._status; }
    set status(value: DirectoryStatus) {
        if (value !== this._status) {
            const oldValue = this._status;
            this._status = value;

            this._onStatusChangedEvent.raise([oldValue, value]);
        }
    }
}

function isNameHidden(name: string): boolean {
    return name.charAt(0) === '.';
}

export interface FileProperties {
    name: string,
    displayName?: string,
    path: string,
    parentPath?: string
    fileId: string;
    fileSizeKb: number;
    status: DocumentStatus;
    ingestionIssues?: IngestionIssues;
    ingestionStatus?: IngestionStatus;
    ingestionWarnings?: IngestionWarnings;
    createdAt: Date;
    // TODO: Below this line should not belong on a file
    clusterId: string;
    folderId: string;
    hasCpml: boolean;
    isHidden: boolean;
    isAssignmentPending?: boolean;
}

export type FileItem = DgFile | DgDirectory;

export class DgFile {
    private _onPropertyChangedEvent: Event<DgFile> = new Event();
    private _properties: FileProperties;

    constructor(info: FileInfo) {
        this._properties = DgFile.createProperties(info);
    }

    get properties(): FileProperties { return this._properties; }
    get onPropertyChangedEvent(): IEvent<DgFile> { return this._onPropertyChangedEvent; }

    updateProperties(properties: Partial<FileProperties>) {
        this._properties = {
            ...this._properties,
            ...properties
        };

        this._properties.folderId = `${this._properties.clusterId}:${this._properties.fileId}`;
        this._onPropertyChangedEvent.raise(this);
    }

    public static createProperties(info: FileInfo): FileProperties {
        if (!info.name) {
            throw new Error("Cannot create a file without a valid name.");
        }

        let name;
        let parentPath;
        if (!!info.displayName) {
            parentPath = getDirectoryPath(info.displayName);
            name = getFileName(info.displayName);
        } else {
            parentPath = getDirectoryPath(info.name);
            name = getFileName(info.name);
        }

        const clusterId = info.clusterId ?? UnassignedDocSetId;
        const fileId = info.id ?? '';
        return {
            name: name,
            displayName: info.displayName,
            path: info.name,
            parentPath: !!parentPath ? parentPath : undefined,
            fileId: fileId,
            fileSizeKb: info.sizeKiloBytes,
            status: info.status,
            clusterId: clusterId,
            folderId: createFolderIdString(clusterId, fileId),
            ingestionIssues: info.ingestionIssues,
            ingestionStatus: info.ingestionStatus,
            ingestionWarnings: info.ingestionWarnings,
            createdAt: new Date(info.createdAt!),
            hasCpml: info.hasCpml,
            isHidden: isNameHidden(name)
        };
    }
}

export class FileManager {
    private readonly _itemLookup: Map<string, DgDirectory | DgFile> = new Map();
    private readonly _pendingItems: Set<string> = new Set();

    private readonly _onFileUpdatedRemoteCallback: (message: FileChangedMessage) => void;
    private readonly _onFileDeletedRemoteCallback: (message: FileChangedMessage) => void;

    private readonly _onItemAdded: Event<DgFile | DgDirectory> = new Event();
    private readonly _onItemRemoved: Event<DgFile | DgDirectory> = new Event();
    private readonly _onFileUpdated: Event<[FileProperties, FileProperties]> = new Event();

    constructor() {
        this.getOrCreateDirectory('');
        this._onFileUpdatedRemoteCallback = this.onFileUpdatedRemote.bind(this);
        this._onFileDeletedRemoteCallback = this.onFileDeletedRemote.bind(this);
        FileUploaderInstance.onFileUpload.addListener(this.onFileUpload.bind(this));
    }

    get onItemAdded(): IEvent<DgFile | DgDirectory> { return this._onItemAdded };
    get onItemRemoved(): IEvent<DgFile | DgDirectory> { return this._onItemRemoved };
    get onFileUpdated(): IEvent<[FileProperties, FileProperties]> { return this._onFileUpdated };
    get items(): Array<DgFile | DgDirectory> { return Array.from(this._itemLookup.values()) }

    configureHub(connection: signalR.HubConnection) {
        connection.on("fileUpdated", this._onFileUpdatedRemoteCallback);
        connection.on("fileDeleted", this._onFileDeletedRemoteCallback);
    }

    async fetchFiles(paths: string[], force: boolean = false): Promise<Array<DgFile | DgDirectory> | null> {
        if (paths.length === 0) {
            return [];
        }

        const result: Set<DgFile | DgDirectory> = new Set();

        const filteredPaths = [];
        const directories: DgDirectory[] = []
        for (const path of paths) {
            const item = this._itemLookup.get(path);
            if (item instanceof DgDirectory) {
                // For now, filter out any directory that we've already initialized or are initializing since we expect delta
                // changes to come over SignalR. This logic can be improved later to refresh stale state or allow the user to
                // force a refresh of the view.
                if (!force && item.status !== DirectoryStatus.Uninitialized) {
                    // We already have this item
                    result.add(item);
                    continue;
                }

                directories.push(item);
            }
            else if ((item instanceof DgFile) && !force) {
                // Already have this item
                result.add(item);
                continue;
            }
            else if (this._pendingItems.has(path)) {
                continue;
            }

            filteredPaths.push(path);
        }

        // Request any remaining paths over the network
        if (filteredPaths.length !== 0) {

            try {
                // Set each directory to initializing
                filteredPaths.forEach(path => this._pendingItems.add(path));
                directories.forEach(dir => dir.status = DirectoryStatus.Initializing);

                // Fetch the file info data from the server
                const fileManagerApi = new FileManagerDataApi();
                const response = await fileManagerApi.fileManagerDataGetFiles(paths);
                const fileInfos: FileInfo[] = response.data;

                for (const info of fileInfos) {
                    if (!info.name) {
                        continue;
                    }

                    const item = this.getOrCreateItem(info);
                    result.add(item);
                }

                // These directories are now initialized
                directories.forEach(dir => dir.status = DirectoryStatus.Initialized);
                this._pendingItems.clear();
            }
            catch (error) {
                directories.forEach(dir => dir.status = DirectoryStatus.Uninitialized);
                this._pendingItems.clear();
                return null;
            }
        }

        return Array.from(result);
    }

    async getFlattenedFileList(paths: string[]): Promise<DgFile[]> {
        return this.fetchFiles(paths, true).then(async items => {
            if (!items?.length) {
                return [];
            }

            const directories = items?.filter(isDgDirectory) || [];
            const files = items?.filter(isDgFile) || [];

            if (!!directories.length) {
                return [...files, ...(await this.getFlattenedFileList(directories.filter(dir => !paths.includes(dir.properties.path)).map(dir => dir.properties.path)))];
            } else {
                return files;
            }
        });
    }

    async assignFiles(docSetId: string, paths: string[]): Promise<void> {
        const revert = this.updateFilesTransaction(paths, { clusterId: docSetId, hasCpml: false, isAssignmentPending: true });

        const resetPendingAssignment = () => {
            this.updateFilesTransaction(paths, { isAssignmentPending: undefined });
        }
        const resetToken = setTimeout(resetPendingAssignment, 60000);

        try {
            const fileManagerDataApi = new FileManagerDataApi();
            await fileManagerDataApi.fileManagerDataAssignFiles(docSetId, paths);
        }
        catch (error) {
            clearTimeout(resetToken);
            revert();
            throw error;
        }
    }

    async unassignFiles(paths: string[]): Promise<void> {
        const revert = this.updateFilesTransaction(paths, { clusterId: UnassignedDocSetId, hasCpml: undefined });

        try {
            const fileManagerDataApi = new FileManagerDataApi();
            await fileManagerDataApi.fileManagerDataUnassignFiles(paths);
        }
        catch (error) {
            revert();
            throw error;
        }
    }

    async fetchErrorFiles(): Promise<DgFile[] | null> {
        try {
            const fileManagerDataApi = new FileManagerDataApi();
            const response = await fileManagerDataApi.fileManagerDataGetErrorFiles();
            const result: DgFile[] = [];
            const fileInfos: FileInfo[] = response.data;

            for (const info of fileInfos) {
                if (!info.name) {
                    continue;
                }

                const item = this.getOrCreateItem(info) as DgFile;
                result.push(item);
            }

            return result;
        }
        catch (error) {
            Logger.LogError(LoggerCategory.FileManager, "Failed to fetch error files.", error);
        }

        return null;
    }

    getDirectory(path: string): DgDirectory | null {
        const result = this._itemLookup.get(path);
        if (result instanceof DgDirectory) {
            return result;
        }

        return null;
    }

    getFile(path: string): DgFile | null {
        const result = this._itemLookup.get(path);
        if (result instanceof DgFile) {
            return result;
        }

        return null;
    }

    getFileFromFolderId(id: string): DgFile | null {
        for (const item of Array.from(this._itemLookup.values())) {
            if (item instanceof DgFile && item.properties.folderId === id) {
                return item;
            }
        }

        return null;
    }

    getFiles(path: string, recursive: boolean): DgFile[] {
        return this.getItems(path, recursive).filter(x => x instanceof DgFile) as DgFile[];
    }

    getDirectories(path: string, recursive: boolean): DgDirectory[] {
        return this.getItems(path, recursive).filter(x => x instanceof DgDirectory) as DgDirectory[];
    }

    getItems(path: string, recursive: boolean): Array<DgFile | DgDirectory> {
        if (isDirectory(path)) {
            return this.items.filter(item => recursive ? isAncestor(path, item.properties) : isChild(path, item.properties));
        }
        else {
            const file = this.getFile(path);
            return !!file ? [file] : [];
        }
    }

    async delete(paths: string[]): Promise<boolean> {
        // TODO: We should support batch delete operations
        let success = true;
        const fileManagerApi = new FileManagerDataApi();
        for (const path of paths) {
            const item = this.removeItemByPath(path);
            try {
                await fileManagerApi.fileManagerDataDelete(path);
            }
            catch (error) {
                if (!!item) {
                    this.addItem(item);
                }
            }
        }

        return success;
    }

    private onFileUpdatedRemote(message: FileChangedMessage) {
        const info = createFileInfoFromDocument(message.document);
        if (!info.name || isDirectory(info.name)) {
            return;
        }

        // This will internally create/update the item appropriately
        this.getOrCreateItem(info);
    }

    private onFileDeletedRemote(message: FileChangedMessage) {
        this.removeItemByPath(message.document.fileId);
    }

    private onFileUpload(event: FileUploadEvent) {
        if (event.status === FileUploadStatus.Started) {
            // Ensure that all ancestors for this file exist so they can be seen in the UX
            let targetPath = event.path;
            while (!!targetPath && !getDirectoryName(targetPath).startsWith('.')) {
                targetPath = getParentDirectoryPath(targetPath);
                this.getOrCreateDirectory(targetPath);
            }
        }
        else if (event.status === FileUploadStatus.Failed) {
            const directory = this.getDirectory(getDirectoryPath(event.path));
            if (!!directory) {
                const itemCount = this.getItems(directory.properties.path, true).length;
                if (itemCount === 0) {
                    this.removeItem(directory);
                }
            }
        }
    }

    public getOrCreateDirectory(path: string): DgDirectory {
        const directoryPath = getDirectoryPath(path);

        let directory = this._itemLookup.get(directoryPath) as DgDirectory;
        if (!directory) {
            directory = new DgDirectory(directoryPath);
            this.addItem(directory);
        }

        return directory;
    }

    private getOrCreateFile(info: FileInfo): DgFile {
        if (isDirectory(info.name!)) {
            throw new Error("Cannot create a file for a directory path.");
        }

        let file = this._itemLookup.get(info.name!) as DgFile;
        if (!file) {
            file = new DgFile(info);
            this.addItem(file);

            // Ensure the parent directories
            let targetPath = file.properties.parentPath;
            while (!!targetPath && !getDirectoryName(targetPath).startsWith('.')) {
                this.getOrCreateDirectory(targetPath);
                targetPath = getParentDirectoryPath(targetPath);
            }

            this.getOrCreateDirectory('');
        }
        else {
            this.updateFile(file, DgFile.createProperties(info));
        }

        return file;
    }

    private getOrCreateItem(info: FileInfo): DgFile | DgDirectory {
        let item: DgFile | DgDirectory;
        if (isDirectory(info.name!)) {
            item = this.getOrCreateDirectory(info.name!);
        }
        else {
            item = this.getOrCreateFile(info);
        }

        return item;
    }

    private removeItemByPath(path: string): DgFile | DgDirectory | undefined {
        const item = this._itemLookup.get(path);
        if (!!item) {
            return this.removeItem(item);
        }

        return undefined;
    }

    private removeItem(item: DgFile | DgDirectory): DgFile | DgDirectory | undefined {
        const deleted = this._itemLookup.delete(item.properties.path);
        if (deleted) {
            this._onItemRemoved.raise(item)
        }

        return deleted ? item : undefined;
    }

    private addItem(item: DgFile | DgDirectory): void {
        this._itemLookup.set(item.properties.path, item);
        this._onItemAdded.raise(item);
    }

    private updateFilesTransaction(paths: string[], props: Partial<FileProperties>): () => void {
        const allFiles = new Set(paths.flatMap(path => this.getFiles(path, true)));
        const filesToDocSet = Array.from(allFiles).map(x => {
            return {
                previousProps: { ...x.properties },
                file: x
            }
        });

        filesToDocSet.forEach(x => {
            this.updateFile(x.file, props);
        });

        return () => {
            filesToDocSet.forEach(x => {
                this.updateFile(x.file, x.previousProps);
            });
        }
    }

    private updateFile(file: DgFile, props: Partial<FileProperties>) {
        const oldProps = { ...file.properties };
        file.updateProperties(props);
        this._onFileUpdated.raise([oldProps, file.properties]);
    }
}

export const FileManagerInstance = new FileManager();
